import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { usePatient } from "../context/PatientContext";
import { useTheme } from "../context/ThemeContext";
import CONFIG from "../config";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Wallet,
  PlusCircle,
  History,
  ChevronRight,
  Calendar,
  Loader2,
  FileText,
  UserCircle2,
  ArrowLeft,
  Phone
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const SelectedPatientDetails = () => {
  const { patientId } = useParams();
  const { doctorInfo, fetchNextCaseId, setPatientData, setSelectedVisit, patientData } = usePatient();
  const { theme, isDarkMode } = useTheme();
  const [patient, setPatient] = useState(null);
  const [patientCases, setPatientCases] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const baseURL = CONFIG.BASE_URL;
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const headers = {
        Authorization: `Bearer ${token}`,
        doctorId: doctorInfo.MANDALI_ID,
        clinicId: doctorInfo.CLINIC_ID,
        roleId: doctorInfo.MANDALI_ROLE,
      };

      try {
        const [patientRes, casesRes, balanceRes] = await Promise.all([
          axios.get(`${baseURL}/patients/${patientId}`, { headers }),
          axios.get(`${baseURL}/patients/${patientId}/cases`, { headers }),
          axios.get(`${baseURL}/patients/${patientId}/balance`, { headers })
        ]);
        console.log(patientRes.data);
        console.log(casesRes.data);
        console.log(balanceRes.data);

        setPatient(patientRes.data);
        setPatientCases(casesRes.data || []);
        setBalance(balanceRes.data.previous_balance || 0);
      } catch (error) {
        console.error("Error fetching patient details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [patientId, baseURL, token, doctorInfo]);

  const handleAddNewCase = async () => {
    try {
      const nextCaseId = await fetchNextCaseId(patientId);
      if (nextCaseId) {
        setPatientData({ ...patientData, ACTION_STATUS: "ADD_NEW_CASE" });
        setSelectedVisit(1);
        navigate(`/visits/${patientId}/${nextCaseId}/1`);
      }
    } catch (error) {
      console.error("Error creating new case:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
        <Loader2 className="animate-spin" size={48} style={{ color: "#ffffff" }} />
      </div>
    );
  }

  if (!patient) return <div className="p-8 text-center" style={{ color: theme.text }}>Patient not found</div>;

  const tableHeaderStyle = {
    backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    color: theme.text,
    borderColor: theme.border,
    fontWeight: "700",
    textTransform: "uppercase",
    fontSize: "11px",
    letterSpacing: "0.15em",
    padding: "1.25rem 2rem",
    borderBottom: `2px solid ${theme.border}`,
    textAlign: "left"
  };

  const tableCellStyle = {
    color: theme.text,
    borderColor: theme.border,
    fontSize: "15px",
    padding: "1.25rem 2rem",
    borderBottom: `1px solid ${theme.border}`
  };

  return (
    <div className="min-h-screen p-4 md:p-8 transition-colors duration-500" style={{ background: 'transparent' }}>
      <div className="max-w-[1200px] mx-auto">

        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all hover:scale-105 active:scale-95"
          style={{
            backgroundColor: "rgba(255,255,255,0.1)",
            color: "#ffffff",
            backdropFilter: "blur(10px)"
          }}
        >
          <ArrowLeft size={18} />
          Back
        </motion.button>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
        >
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-[2rem] flex items-center justify-center shadow-2xl border"
              style={{ backgroundColor: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.3)", backdropFilter: "blur(10px)" }}>
              <User size={32} color="#ffffff" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-serif mb-1" style={{ color: "#ffffff", fontFamily: theme.fonts?.serif }}>
                {patient.PATIENT_NAME}
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-lg opacity-80 flex items-center gap-2" style={{ color: "#ffffff" }}>
                  <span className="px-2 py-0.5 rounded-md bg-white/10 text-sm font-mono">ID: {patient.PATIENT_ID}</span>
                  {patient.PATIENT_AGE && <span>• {patient.PATIENT_AGE} Yrs</span>}
                  {patient.PATIENT_GENDER && <span>• {patient.PATIENT_GENDER}</span>}
                </p>
                {patient.PATIENT_PHONE && (
                  <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
                    <Phone size={14} color="#ffffff" className="opacity-80" />
                    <span className="text-sm font-bold text-white mb-0.5">{patient.PATIENT_PHONE}</span>
                    <div
                      className="ml-1 p-1.5 rounded-lg cursor-pointer transition-all hover:scale-110 active:scale-95 flex items-center justify-center"
                      style={{ backgroundColor: '#25D36620', color: '#25D366', border: '1px solid #25D36640' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const cleanNumber = patient.PATIENT_PHONE.replace(/\D/g, '');
                        window.open(`https://wa.me/${cleanNumber}`, '_blank');
                      }}
                      title="Open in WhatsApp"
                    >
                      <FaWhatsapp size={14} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="px-6 py-4 rounded-3xl border shadow-xl flex flex-col items-end"
              style={{ backgroundColor: "rgba(255,255,255,0.1)", borderColor: "rgba(255,255,255,0.2)", backdropFilter: "blur(10px)" }}>
              <span className="text-xs uppercase tracking-widest opacity-60 font-bold mb-1" style={{ color: "#ffffff" }}>Account Balance</span>
              <div className="flex items-center gap-2">
                <Wallet size={20} color="#ffffff" className="opacity-60" />
                <span className="text-2xl font-bold" style={{ color: balance >= 0 ? "#ffffff" : "#ff7675" }}>
                  ₹ {balance?.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Bar */}
        <div className="mb-8 flex justify-between items-end">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl" style={{ backgroundColor: theme.secondary + '40', color: "#ffffff" }}>
              <History size={20} />
            </div>
            <h3 className="text-xl font-bold" style={{ color: "#ffffff" }}>Visit History</h3>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAddNewCase}
            className="flex items-center gap-2 px-6 py-4 rounded-2xl font-bold shadow-2xl transition-all"
            style={{
              backgroundColor: theme.secondary,
              color: "#ffffff",
            }}
          >
            <PlusCircle size={20} />
            Add New Case
          </motion.button>
        </div>

        {/* Case List Table */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-[2.5rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] border"
          style={{ backgroundColor: theme.card, borderColor: theme.border }}
        >
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ backgroundColor: theme.inputBg }}>
              <thead>
                <tr style={{ backgroundColor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)' }}>
                  <th style={tableHeaderStyle}>Case Information</th>
                  <th style={tableHeaderStyle}>Date & Status</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: theme.border }}>
                {patientCases.length > 0 ? (
                  patientCases.map((caseItem, idx) => (
                    <motion.tr
                      key={caseItem.CASE_ID}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => navigate(`/visits/${patientId}/${caseItem.CASE_ID}`, { state: { patientName: patient.PATIENT_NAME } })}
                      className="group cursor-pointer transition-colors"
                      style={{
                        borderBottom: `1px solid ${theme.border}`,
                        backgroundColor: isDarkMode ? theme.inputBg : 'inherit'
                      }}
                      whileHover={{
                        backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)"
                      }}
                    >
                      <td style={tableCellStyle}>
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-2xl flex items-center justify-center font-bold text-lg border shadow-sm"
                            style={{ backgroundColor: theme.primary + '15', color: theme.primary, borderColor: theme.primary + '20' }}>
                            <FileText size={20} />
                          </div>
                          <div>
                            <p className="font-bold text-lg" style={{ color: theme.text }}>Case #{caseItem.CASE_ID}</p>
                            <p className="text-xs font-semibold opacity-60 uppercase tracking-wider" style={{ color: theme.textSecondary }}>Medical Record</p>
                          </div>
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primary + '10' }}>
                            <Calendar size={16} style={{ color: theme.primary }} className="opacity-70" />
                          </div>
                          <span className="font-semibold text-base" style={{ color: theme.text }}>
                            {caseItem.CREATED_AT ? new Date(caseItem.CREATED_AT).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : "Recently Added"}
                          </span>
                        </div>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "right" }}>
                        <button
                          className="px-6 py-2.5 rounded-full text-sm font-bold border transition-all duration-200 group-hover:bg-primary/10 flex items-center justify-center gap-2 float-right shadow-sm"
                          style={{
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            borderColor: theme.border,
                            color: theme.primary
                          }}
                        >
                          View Details
                          <ChevronRight size={14} />
                        </button>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr className="border-0" style={{ backgroundColor: 'transparent' }}>
                    <td colSpan={3} className="py-24 text-center">
                      <div className="w-20 h-20 rounded-full bg-slate-100/10 flex items-center justify-center mx-auto mb-4 border border-dashed border-slate-500/20">
                        <History className="opacity-20" size={40} style={{ color: theme.text }} />
                      </div>
                      <p className="text-xl font-bold opacity-30" style={{ color: theme.text }}>No medical history found for this patient</p>
                      <button
                        onClick={() => navigate(`/visits/${patientId}/addcase`)}
                        className="mt-4 text-sm font-bold underline"
                        style={{ color: theme.primary }}
                      >
                        Create first case record
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Footer info */}
        <div className="mt-12 text-center text-xs opacity-30 font-medium tracking-widest uppercase" style={{ color: "#ffffff" }}>
          Patient Management System • Confidential Record
        </div>
      </div>
    </div>
  );
};

export default SelectedPatientDetails;

